import { useState } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Smartphone, MessageSquare, KeyRound, Copy, Check, AlertCircle, ShieldCheck } from 'lucide-react'
import { authAPI } from '../../services/api'
import { ResponsiveWrapper } from '../../components/layout'
import { useI18n } from '../../i18n'
import useTwoFactorStatus from '../../hooks/useTwoFactorStatus'
import { twoFactorPosture, POSTURE_LOADING, POSTURE_ERROR, POSTURE_SSO, POSTURE_ON } from '../../utils/securityPosture'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import CurrentFactorField, { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { currentCodeProblem, currentCodeMayBeSpent, currentFactorRefusal, normalizeCurrentCode, refusalSentence } from '../../utils/currentFactor'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * SecurityScreen v238.21.0 — two-factor authentication management.
 * Session PIN now lives in the main Settings menu (/settings/session-pin);
 * change password has its own screen (/settings/change-password).
 */
export default function SecurityScreen() {
  const navigate = useNavigate()
  const goBack = useMenuBack()
  const { t } = useI18n()

  // ★★★ v242.132.0 — ONE READER, SHARED WITH THE SETTINGS ROW, AND `error` IS
  //     ITS OWN STATE. This screen used to catch a failed status fetch, log it,
  //     and leave `status` null — which renders identically to "you have no 2FA".
  //     A patient with 2FA on, on a flaky connection, was told their account was
  //     unprotected and offered a button to set up what they already had. A
  //     silent catch is the house defect; on a security surface it does not
  //     produce a wrong number, it produces a wrong statement about protection.
  const { status, error, loading, reload } = useTwoFactorStatus()
  const posture = twoFactorPosture({ status, error, loading })
  const [regenCodes, setRegenCodes] = useState(null)
  const [regenLoading, setRegenLoading] = useState(false)
  const [copiedCodes, setCopiedCodes] = useState(false)
  // PROD-1154: 2FA is on here, so new backup codes need a CURRENT code first (utils/currentFactor.js).
  const [regenAsk, setRegenAsk] = useState(false)
  const [regenCurrentCode, setRegenCurrentCode] = useState('')
  const [regenError, setRegenError] = useState('')
  const [regenLockSeconds, startRegenLock] = useLockCountdown()

  // PROD-1154 (review M1): the first tap asks only "Continue?" and regenerates WITHOUT a code, as before. The
  //   field opens only when the API asks (403 2fa_current_factor_required, nothing used up); the live API
  //   (v243.369.0) never asks, so there the new codes come at once.
  const handleRegenerate = async () => {
    if (!confirm('This will invalidate all your existing backup codes and generate new ones. Continue?')) return
    setRegenCurrentCode('')
    await runRegenerate(undefined)
  }

  const cancelRegenerate = () => {
    setRegenAsk(false)
    setRegenCurrentCode('')
    setRegenError('')
  }

  // PROD-1154: once the API has asked, the typed current code goes out; nothing is sent without one that can be one.
  const submitRegenerate = async () => {
    // Review L-1: an empty field gets no red line; the field's own grey line already says a code is needed.
    const problem = currentCodeProblem(true, regenCurrentCode)
    if (problem) { setRegenError(refusalSentence(t, problem)); return }
    await runRegenerate(normalizeCurrentCode(regenCurrentCode))
  }

  // PROD-1154: the one regenerate call, with or without a current code. Refusals by `code`; the lockout counts down;
  //   a code that may have been used up is emptied (review L1), so a retry cannot spend an attempt on it.
  const runRegenerate = async (sentCode) => {
    setRegenError('')
    setRegenLoading(true)
    try {
      const response = await authAPI.regenerateBackupCodes(sentCode)
      setRegenCodes(response.backupCodes)
      setRegenAsk(false)
      setRegenCurrentCode('')
      // Re-read rather than patch a local copy — the server is the source.
      reload()
    } catch (err) {
      if (sentCode && currentCodeMayBeSpent(err)) setRegenCurrentCode('')
      const refused = currentFactorRefusal(err)
      if (refused && (refused.ask || refused.locked)) setRegenAsk(true)
      if (refused?.locked) startRegenLock(refused.seconds)
      else if (refused) setRegenError(refusalSentence(t, refused.code))
      else setRegenError(apiErrorMessage(err, t('errors2.failedToRegenerateCodes')))
    } finally {
      setRegenLoading(false)
    }
  }

  const handleCopyCodes = () => {
    if (!regenCodes) return
    navigator.clipboard.writeText(regenCodes.join('\n')).then(() => {
      setCopiedCodes(true)
      setTimeout(() => setCopiedCodes(false), 3000)
    })
  }

  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
    <div className="screen-min-h bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => goBack()} className="p-2 -ml-2 text-gray-600">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold text-orella-700">{t('security.security')}</h1>
      </div>

      <div className="flex-1 px-4 pb-8 overflow-auto">

        {/* ===== 2FA / SSO SECTION ===== */}
        <div className="mb-4 mt-2">
          {posture === POSTURE_LOADING ? (
            <div className="py-6 text-center">
              <OrellaSpinner size={24} className="mx-auto" />
            </div>
          ) : posture === POSTURE_ERROR ? (
            /* ★★★ NOT "not set up". We could not READ the state, which is a
               different thing, and saying the wrong one here is the defect. */
            <div className="py-6 text-center">
              <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-gray-700 mb-1">
                {t('security.couldNotCheck', { defaultValue: "We couldn't check your security settings." })}
              </p>
              <p className="text-xs text-gray-500 mb-3">
                {t('security.couldNotCheckBody', { defaultValue: 'This does not change your account. Please try again.' })}
              </p>
              <button
                onClick={reload}
                className="px-6 py-2.5 bg-orella-500 text-white rounded-full font-semibold text-sm"
              >
                {t('security.tryAgain', { defaultValue: 'Try again' })}
              </button>
            </div>
          ) : posture === POSTURE_SSO ? (
            /* ── SSO-Only User: Show provider badge instead of 2FA ── */
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-green-50 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{t('security.accountVerification')}</p>
                  <p className="text-xs text-gray-500">{t('security.howYourIdentityIsVerified')}</p>
                </div>
                <span className="text-xs font-medium text-green-700 bg-green-100 px-2.5 py-0.5 rounded-full">{t('security.active')}</span>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: status.authProvider?.includes('google') ? '#4285F4' : '#000' }}>
                    {status.authProvider?.includes('google') ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" opacity=".8"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#fff" opacity=".6"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" opacity=".8"/></svg>
                    ) : (
                      <svg width="18" height="22" viewBox="0 0 170 200" fill="white"><path d="M150.4 172.3c-7.6 16.3-11.3 23.5-21.1 38-13.7 20.2-33.1 45.3-57.1 45.5-21.3.2-26.7-13.9-55.6-13.7-28.8.2-34.8 14.1-56.1 13.9-24-.2-42.2-23-55.9-43.1C-125 163.4-133.4 63-84.2 14.6c17.3-17 40.8-27 63.3-27 23.5 0 38.3 14 57.7 14 18.8 0 30.2-14 57.2-14 20 0 41 10.7 58.3 29.2-51.2 28-42.9 101-2 115.5z"/></svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 text-sm">
                      {t('security.securedBy', 'Secured by')} {status.authProvider?.includes('google') ? 'Google' : 'Apple'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('security.identityVerifiedThrough', 'Your identity is verified through')} {status.authProvider?.includes('google') ? 'Google' : 'Apple'} every time you sign in
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center mb-4">
                {t('security.twoFactorHandledByProvider', 'Two-factor authentication is handled by your sign-in provider and cannot be changed here.')}
              </p>
            </>
          ) : (
            /* ── Email/Password User: Standard 2FA Management ── */
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-orella-50 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-orella-500" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{t('security.twofactorAuthentication2')}</p>
                  <p className="text-xs text-gray-500">{t('security.protectsYourHealthInformation')}</p>
                </div>
                {posture === POSTURE_ON ? (
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2.5 py-0.5 rounded-full">{t('security.active')}</span>
                ) : (
                  <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">{t('security.notSetUp')}</span>
                )}
              </div>

              {posture === POSTURE_ON ? (
                <>
                  {/* Current method */}
                  <div className="bg-gray-50 rounded-2xl p-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orella-500 flex items-center justify-center">
                        {status.method === 'sms' 
                          ? <MessageSquare className="w-5 h-5 text-white" />
                          : <Smartphone className="w-5 h-5 text-white" />
                        }
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">
                          {status.method === 'sms' ? 'Text message (SMS)' : 'Authenticator app'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {status.method === 'sms' ? 'Code sent via text each sign-in' : 'Google Authenticator, Authy, etc.'}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => navigate('/security', { state: { resetup: true, currentMethod: status.method } })}
                      className="mt-3 text-sm font-medium text-orella-500"
                    >
                      {t('security.changeVerificationMethod')}
                    </button>
                  </div>

                  {/* Backup codes */}
                  <div className="bg-gray-50 rounded-2xl p-4 mb-3">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">{t('security.backupCodes')}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${((status.backupCodesRemaining || 0) / 10) * 100}%`,
                                backgroundColor: (status.backupCodesRemaining || 0) <= 3 ? '#EF9F27' : '#66D170'
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{status.backupCodesRemaining || 0} of 10</span>
                        </div>
                      </div>
                    </div>

                    {status.backupCodesRemaining <= 3 && status.backupCodesRemaining > 0 && !regenCodes && (
                      <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700">{t('security.runningLowRegenerateANew')}</span>
                      </div>
                    )}

                    {/* PROD-1154: a failure shows whether or not the API has asked for a code yet */}
                    {!regenLockSeconds && regenError && (
                      <div role="alert" className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">{regenError}</div>
                    )}

                    {/* PROD-1154: a CURRENT code, once the API has asked for one (403 required) */}
                    {regenAsk && (
                      <div className="mb-3">
                        <LockNotice seconds={regenLockSeconds} />
                        <CurrentFactorField
                          value={regenCurrentCode}
                          onChange={(v) => { setRegenCurrentCode(v); setRegenError('') }}
                          method={status.method}
                          disabled={regenLoading || !!regenLockSeconds}
                          autoFocus
                          onEnter={submitRegenerate}
                        />
                      </div>
                    )}

                    <button 
                      onClick={regenAsk ? submitRegenerate : handleRegenerate}
                      disabled={regenLoading || (regenAsk && !!regenLockSeconds)}
                      className="w-full py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 disabled:opacity-50"
                    >
                      {regenLoading ? 'Generating...' : 'Regenerate backup codes'}
                    </button>
                    {regenAsk && (
                      <button
                        type="button"
                        onClick={cancelRegenerate}
                        disabled={regenLoading}
                        className="w-full mt-2 py-2 text-sm font-medium text-gray-500 disabled:opacity-50"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    )}
                  </div>

                  {/* Regenerated codes */}
                  {regenCodes && (
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800 text-sm">{t('security.newBackupCodes')}</h3>
                        <button onClick={handleCopyCodes} className="flex items-center gap-1.5 text-xs font-medium text-orella-500">
                          {copiedCodes ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedCodes ? 'Copied!' : 'Copy all'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {regenCodes.map((code, i) => (
                          <div key={i} className="font-mono text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg text-center border border-gray-100">
                            {code}
                          </div>
                        ))}
                      </div>
                      <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl mb-3">
                        <p className="text-xs text-amber-700">{t('security.saveTheseCodesSomewhereSafe')}</p>
                      </div>
                      <button onClick={() => setRegenCodes(null)} className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold text-sm">
                        I've saved my codes
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 text-center mb-4">
                    2FA is required and cannot be disabled.
                  </p>
                </>
              ) : (
                <div className="text-center py-4 mb-4">
                  <p className="text-sm text-gray-600 mb-3">{t('security.twofactorAuthenticationIsNotSet')}</p>
                  <button 
                    onClick={() => navigate('/security', { replace: true })}
                    className="px-6 py-2.5 bg-orella-500 text-white rounded-full font-semibold text-sm"
                  >
                    {t('security.setUp2fa')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </ResponsiveWrapper>
  )
}
